import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TableTab from "../../../components/tabs/table-tab";
import { REPORTS_FILTER_DATE_TIME_CURRENT_WEEK } from "../../../constants/element-type";
import { FILTER_DATE, FILTER_SITES, FILTER_TAGHOLDERS } from "../../../constants/filter-types";
import { HOURS_WORKED } from "../../../constants/menu-types";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import { DEF_PG_SIZE_REPORTS } from '../../../constants/maximum-page-size';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { ColumnFormattingClasses } from "../../../models/ColumnDefinitions/constants/ColumnFormattingClasses";
import HoursWorkedReportColumn from "../../../models/ColumnDefinitions/HoursWorkedReportColumn";
import { Filter } from "../../../models/Filter";
import { ListQuery } from "../../../models/ListQuery";
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import AccessAreaSearch from "../../../models/search/AccessAreaSearch";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import HoursWorkedReportSearch from "../../../models/search/HoursWorkedReportSearch";
import EnrolmentSearch from "../../../models/search/EnrolmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { RowTooltipConfig } from "../../../models/table/RowTooltipConfig";
import { getAccessAreaNamesWithPagination } from "../../../services/accessAreaService";
import { auditRunReport } from "../../../services/auditService";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";

import { getDepartmentNamesWithPagination } from "../../../services/departmentService";

import { getEnrolmentNamesWithPagination } from "../../../services/enrolmentService";
import { getHoursWorkedReport } from "../../../services/reportService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getUserFiltersByView } from "../../../services/userPreferenceService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { getCurrentWeekDates } from "../../../utilities/DateUtils";
import { makeFilterOnlyColumns, removeColumns } from "@/models/utilities/columnHelper";

interface HoursWorkedReportProps {
    reportId: string;
}

const HoursWorkedReport: FC<HoursWorkedReportProps> = ({ reportId }) => {
    const { translate } = useLocale();

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const currentMenuDetail = HOURS_WORKED;

    const lookup: Lookup[] = useMemo(() => [
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?: ListQuery) => getAccessAreaNamesWithPagination(null, listQuery),
            searchAccessor: AccessAreaSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: "profile",
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: "masterId",
            values: [],
            dataMethod: getEnrolmentNamesWithPagination,
            searchAccessor: EnrolmentSearch.find(column => column.accessor === "name")?.searchOnName
        },
        {
            name: "siteId",
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(column => column.accessor === "name")?.searchOnName
        },
    ], []);

    // static filters — returns the default (empty) state for each static filter so it can be
    // restored after the user clears it rather than disappearing entirely.
    const getOriginalStaticFilter = async (filterKey: string) => {
        const date = new Date();
        switch (filterKey) {
            case "dateOnly":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    clearPolicy: "locked",
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.date),
                    options: [],
                    selected: [{
                        elementSysname: REPORTS_FILTER_DATE_TIME_CURRENT_WEEK,
                        label: translate.byKey(TranslationKeys.currentWeek),
                        value: getCurrentWeekDates(date),
                    }],
                };
            case "siteId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.site),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            case "masterId":
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: translate.byKey(TranslationKeys.displayName),
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
            default:
                return {
                    isCustomFilter: true,
                    isStatic: true,
                    key: filterKey,
                    label: filterKey,
                    options: [],
                    selected: [{ ignore: true, label: translate.byKey(TranslationKeys.all), value: '*' }],
                };
        }
    }

    const initialiseUserPreferences = async (setDetails: (userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys) => void) => {
        const staticFilterKeys = ["dateOnly", "siteId", "masterId"];
        let customFilter: Filter[] = [];

        try {
            const savedFilters = await getUserFiltersByView(userLoginId, currentMenuDetail, reportId);

            if (!savedFilters.find(f => f.filterId === FILTER_DATE)) {
                customFilter.push(await getOriginalStaticFilter("dateOnly"));
            }
            if (!savedFilters.find(f => f.filterId === FILTER_SITES)) {
                customFilter.push(await getOriginalStaticFilter("siteId"));
            }
            if (!savedFilters.find(f => f.filterId === FILTER_TAGHOLDERS)) {
                customFilter.push(await getOriginalStaticFilter("masterId"));
            }
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setDetails(userLoginId, currentMenuDetail, customFilter, reportId, staticFilterKeys);
        }
    }
    // <-- end context

    const getDataMethod = async (listParameters?: ListQuery) => {
        try {
            await auditRunReport(reportId);
            return getHoursWorkedReport(translate, listParameters);
        } catch (error) {
            return Promise.reject(getErrorMessage(error));
        }
    }

    return (<TableContextProvider
        maxPageSize={DEF_PG_SIZE_REPORTS}
        dataMethod={getDataMethod}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={makeFilterOnlyColumns(removeColumns(HoursWorkedReportColumn, ["notes", "segments", "timeSpan", "shiftType"]), ['dateOnly', 'dayOfWeek', 'firstIn', 'lastOut', 'hoursWorked'])}
        searchDescriptor={HoursWorkedReportSearch}
        childColumns={removeColumns(HoursWorkedReportColumn, ['title', 'displayName', 'firstName', 'middleName', 'lastName', 'idNumber', 'shiftType', 'daysWorked', 'totalHoursWorked', 'avgHoursPerDay', 'avgFirstInTime', 'avgLastOutTime', 'timeSpan', 'segments', 'notes', 'company', 'department', 'profileName', 'site'])}
        useFilter
        useSort
        usePagination
        useSearch
        useTable
        lookup={lookup}
        loadDataOnDemand
        runDataOnDemandButtonLabel={translate.byKey(TranslationKeys.runReport)}
        getOriginalStaticFilter={getOriginalStaticFilter}
        rowTooltipConfig={{ textAccessor: 'segments', triggerColumn: 'dateOnly', errorCondition: (row) => row.notes === 'err', textSeparator: ',' } satisfies RowTooltipConfig}>
        <TableTab
            title={''}
            reportTitle={translate.byKey(TranslationKeys.hoursWorked)}
            selectRows={false}
            showFilterMenu={true}
            useGroupedRows
            highlightExpandedParent
            childTableHeading={translate.byKey(TranslationKeys.workingHoursHistory)}
            cyName="hoursWorkedReport"
            customRowColourWithRange={{ fieldName: "isDefault", comparisonValue: false, colour1: ColumnFormattingClasses.categoryGrey }} />
    </TableContextProvider >
    );
};

export default HoursWorkedReport;

